import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryCacheProps } from './hookCommon';
import useNotification from './useNotification';
import { http } from '../utils';
import { GM_API_SERVER } from '../config';
import { GmProposalParams } from '@peeramid-labs/sdk';
import { Address, Hex } from 'viem';
import { UserProfile } from '../types/user';
import { processApiError } from '../utils/processError';
import { JoinRequirement } from '../types/tokenGatedAccess';
import { Fellowship } from '../types/fellowship';
import { ThreadData } from '../types/thread';
import { useWeb3Context } from '../providers/Web3Provider/useWeb3Context';
import { AutomatedGameParameters, CreateAutomatedGamePayload } from '../types/automatedGames';

const ApiUrl = GM_API_SERVER;

export const useGameMasterBackend = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const web3Context = useWeb3Context();

  // Helper function to process API errors with auth failure handling.
  // Suppresses notifications during logout to avoid "Authentication failed" toasts
  // from stale requests that return 403 while disconnecting.
  const handleApiError = (error: any) => {
    if (web3Context.isLoggingOut()) return;
    processApiError(error, notification, true, web3Context.logout);
  };

  const useMyProposalId = ({ rankifyInstanceAddress, gameId }: { rankifyInstanceAddress: Address; gameId: number }) =>
    useQuery({
      queryKey: ['GMBackend', 'myProposalId', rankifyInstanceAddress, gameId],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/player/proposal/id/${rankifyInstanceAddress}/${gameId}`,
          }).then((r) => r.data);
        } catch (e: any) {
          if (e.status === 404) {
            return -1;
          } else {
            handleApiError(e);
          }
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
    });

  const useUserProfile = ({ userAddress, enabled = true }: { userAddress: Address; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'userProfile', userAddress],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/user/profile/${userAddress}`,
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled,
    });

  const useUserInfo = ({ userAddress, enabled = true }: { userAddress: Address; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'userInfo', userAddress],
      queryFn: async () => {
        try {
          return await http(
            {
              method: 'GET',
              url: `${ApiUrl}/user/public/info/${userAddress}`,
            },
            true
          ).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled,
    });

  const useMyActiveThreads = ({
    fellowshipId,
    enabled = true,
  }: {
    fellowshipId?: number;
    enabled?: boolean;
  } = {}) => {
    const userAddress = web3Context.account as Address;

    return useQuery({
      queryKey: ['GMBackend', 'myActiveThreads', userAddress, fellowshipId],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/user/fellowship-act/active-threads`,
            params: fellowshipId ? { fellowshipId } : undefined,
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: 30000, // Refetch every 30 seconds
      enabled: !!userAddress && enabled,
    });
  };

  const useLastThreadParticipantChannel = ({ address }: { address: Address }) =>
    useQuery({
      queryKey: ['GMBackend', 'lastThreadParticipantChannel', address],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/thread/participant/last/${address}`,
          }).then((r) => r.data);
        } catch (e: any) {
          if (e.status === 404) {
            return null;
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
    });

  const useDiscordCheckSubscription = ({ guildId }: { guildId: string }) =>
    useQuery({
      queryKey: ['GMBackend', 'discordSubscription', guildId],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/user/profile/discord/subscription/${guildId}`,
          }).then((r) => r.data);
        } catch (e: any) {
          if (e.status === 404) {
            return { isSubscribed: false };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
    });

  const useTelegramCheckSubscription = ({ chatId }: { chatId: string }) =>
    useQuery({
      queryKey: ['GMBackend', 'telegramSubscription', chatId],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/user/profile/telegram/subscription/${chatId}`,
          }).then((r) => r.data);
        } catch (e: any) {
          if (e.status === 404) {
            return { isSubscribed: false };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
    });

  const attestProposalMutation = useMutation({
    mutationFn: async ({
      instanceAddress,
      gameId,
      turn,
      proposal,
    }: {
      instanceAddress: Address;
      gameId: number;
      turn: number;
      proposal: string;
    }) => {
      const response = await http(
        {
          method: 'POST',
          url: `${ApiUrl}/player/proposal/${instanceAddress}/${gameId.toString()}/${turn.toString()}/attestProposal`,
          data: { proposal },
        },
        false,
        web3Context
      );

      return response.data as {
        submissionParams: GmProposalParams;
      };
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const submitProposalMutation = useMutation({
    mutationFn: async ({
      rankifyInstanceAddress,
      gmProposalParams,
      proposerSignature,
    }: {
      rankifyInstanceAddress: Address;
      gmProposalParams: GmProposalParams;
      proposerSignature: Hex;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/player/proposal/${rankifyInstanceAddress}/${gmProposalParams.gameId.toString()}`,
        data: { submissionParams: gmProposalParams, proposerSignature },
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const attestVoteMutation = useMutation({
    mutationFn: async ({
      instanceAddress,
      gameId,
      turn,
      vote,
    }: {
      instanceAddress: Address;
      gameId: number;
      turn: number;
      vote: number[];
    }) => {
      const response = await http(
        {
          method: 'POST',
          url: `${ApiUrl}/player/vote/${instanceAddress}/${gameId.toString()}/${turn.toString()}/attestVote`,
          data: { vote },
        },
        false,
        web3Context
      );

      return response.data as {
        playerSalt: Hex;
        ballotId: string;
        ballotHash: Hex;
      };
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const submitVotesMutation = useMutation({
    mutationFn: async ({
      rankifyInstanceAddress,
      vote,
      gameId,
      ballotHash,
      voterSignature,
      ballotId,
    }: {
      rankifyInstanceAddress: Address;
      vote: number[];
      gameId: number;
      ballotHash: Hex;
      voterSignature: Hex;
      ballotId: string;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/player/vote/${rankifyInstanceAddress}/${gameId.toString()}`,
        data: { vote, ballotHash, voterSignature, ballotId },
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const attestJoinGameMutation = useMutation({
    mutationFn: async ({ instanceAddress, gameId }: { instanceAddress: Address; gameId: number }) => {
      const response = await http(
        {
          method: 'POST',
          url: `${ApiUrl}/thread/actions/game/${instanceAddress}/${gameId.toString()}/attestJoinGame`,
          data: {},
        },
        false,
        web3Context
      );
      const data = response.data;

      return data as {
        signature: Address;
        gmCommitment: Hex;
        deadline: number;
        participantPubKey: Hex;
      };
    },
    onError: (e: any) => {
      processApiError(e);
    },
  });

  const uploadToIpfsMutation = useMutation({
    mutationFn: async ({ metadata }: { metadata: object }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/user/actions/ipfs/upload`,
        data: metadata,
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const storeUserMutation = useMutation({
    mutationFn: async ({ userProfile }: { userProfile: UserProfile }) => {
      return await http(
        {
          method: 'POST',
          url: `${ApiUrl}/user/profile/store`,
          data: userProfile,
        },
        false,
        web3Context
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'userProfile', variables.userProfile.address],
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const storeThreadMutation = useMutation({
    mutationFn: async ({ threadData, requirements }: { threadData: ThreadData; requirements?: JoinRequirement[] }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/thread/owner/store`,
        data: {
          ...threadData,
          requirements: requirements?.map((r) => ({
            ...r,
            blockNumber: r.blockNumber.toString(),
            amount: r.amount.toString(),
            tokenId: r.tokenId?.toString(),
          })),
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'lastThreadParticipantChannel', variables.threadData.owner],
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadByCode', variables.threadData.code, false],
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadByCode', variables.threadData.code, true],
      });
      //invalidate all thread list queries that starts with threadList
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'GMBackend' && query.queryKey[1] === 'threadList',
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'myActiveThreads'],
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const deleteThreadMutation = useMutation({
    mutationFn: async ({ id, fellowshipId }: { id: number; fellowshipId: number }) => {
      return await http({
        method: 'DELETE',
        url: `${ApiUrl}/thread/owner/${id}/${fellowshipId}`,
      });
    },
    onSuccess: (_, variables) => {
      console.log('Thread deleted successfully');
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'GMBackend' && query.queryKey[1] === 'threadList',
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'myActiveThreads'],
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadGroups', variables.fellowshipId],
      });
    },
    onError: (e: any) => {
      console.error('Error deleting thread:', e);
      handleApiError(e);
    },
  });

  const storeThreadParticipantMutation = useMutation({
    mutationFn: async ({
      participantData,
    }: {
      participantData: {
        threadId: number;
        fellowshipId: number;
        participant: Address;
        channelId?: number;
        instanceAddress: Address;
      };
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/thread/participant/store`,
        data: participantData,
      });
    },
    onSuccess: (_, variables) => {
      console.log('Thread participant stored successfully');
      queryClient.invalidateQueries({
        queryKey: [
          'GMBackend',
          'threadParticipant',
          variables.participantData.threadId,
          variables.participantData.fellowshipId,
        ],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'GMBackend' && query.queryKey[1] === 'threadList',
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'myActiveThreads'],
      });
    },
    onError: (e: any) => {
      console.error('Error storing thread participant:', e);
      handleApiError(e);
    },
  });

  const deleteThreadParticipantMutation = useMutation({
    mutationFn: async ({
      participant,
      threadId,
      fellowshipId,
    }: {
      participant: Address;
      threadId: number;
      fellowshipId: number;
    }) => {
      return await http({
        method: 'DELETE',
        url: `${ApiUrl}/thread/participant/${participant}/${threadId}/${fellowshipId}`,
      });
    },
    onSuccess: (_, variables) => {
      console.log('Thread participant deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadParticipant', variables.threadId, variables.fellowshipId],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'GMBackend' && query.queryKey[1] === 'threadList',
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'myActiveThreads'],
      });
    },
    onError: (e: any) => {
      console.error('Error deleting thread participant:', e);
      handleApiError(e);
    },
  });

  const endTurnMutation = useMutation({
    mutationFn: async ({ instanceAddress, gameId }: { instanceAddress: Address; gameId: number }) => {
      try {
        return await http({
          method: 'POST',
          url: `${ApiUrl}/endTurn/${instanceAddress}/${gameId.toString()}`,
        });
      } catch (e) {}
    },
    onSuccess: () => {},
    onError: () => {},
  });

  const useFellowshipData = () =>
    useQuery<{
      id: string | null;
      parentFellowshipId: string | null;
      instanceAddress: string | null;
      maoDistName: string | null;
    }>({
      queryKey: ['GMBackend', 'fellowshipData'],
      queryFn: async () => {
        try {
          return await http({
            method: 'POST',
            url: `${ApiUrl}/fellowship/reserve`,
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: (data) => {
        console.log('data', data);
        return !data?.state?.data?.id ? 3000 : false;
      },
      gcTime: 30000,
      staleTime: 5000,
    });

  const useThreadByCode = ({
    code,
    auth = false,
    enabled = true,
  }: {
    code: string;
    auth?: boolean;
    enabled?: boolean;
  }) =>
    useQuery({
      queryKey: ['GMBackend', 'threadByCode', code?.endsWith('/') ? code.slice(0, -1) : code, auth],
      queryFn: async () => {
        try {
          const response = await http(
            {
              method: 'GET',
              url: `${ApiUrl}/thread/read/${code}`,
            },
            !auth // Only disable credentials if auth is explicitly false
          );
          const threadData = response.data;

          // Parse trackData if it exists and is a string
          if (threadData && threadData.playlist && threadData.playlist.tracks) {
            threadData.playlist.tracks = threadData.playlist.tracks.map((track: any) => {
              if (track.trackData && typeof track.trackData === 'string') {
                try {
                  return {
                    ...track,
                    trackData: JSON.parse(track.trackData),
                  };
                } catch (e) {
                  console.error('Failed to parse trackData for track:', track.spotifyId, e);
                  // Keep original trackData or set to null if parsing fails
                  return { ...track, trackData: null };
                }
              }
              return track;
            });
          }
          return threadData;
        } catch (e: any) {
          if (e.status === 404) {
            return null;
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!code && enabled,
    });

  const useChains = () =>
    useQuery({
      queryKey: ['GMBackend', 'chains'],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/chains`,
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
    });

  const useValidateJoinRequirement = ({
    chainId,
    amount,
    blockNumber,
    contractAddress,
  }: {
    chainId: number;
    amount: bigint;
    blockNumber: bigint;
    contractAddress?: string;
  }) =>
    useQuery({
      queryKey: [
        'GMBackend',
        'validateJoinRequirement',
        chainId,
        amount.toString(),
        blockNumber.toString(),
        contractAddress,
      ],
      queryFn: async () => {
        try {
          return await http({
            method: 'POST',
            url: `${ApiUrl}/thread/actions/validate-requirement`,
            data: {
              chainId,
              amount: amount.toString(),
              blockNumber: blockNumber.toString(),
              contractAddress,
            },
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!chainId && !!amount,
    });

  const useValidateSpotifyTrack = ({ link }: { link: string }) =>
    useQuery({
      queryKey: ['GMBackend', 'validateSpotifyTrack', link],
      queryFn: async () => {
        try {
          return await http({
            method: 'GET',
            url: `${ApiUrl}/user/actions/spotify/track`,
            params: { link },
          }).then((r) => r.data);
        } catch (e) {
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!link,
    });

  const useFellowshipByArena = (arena: string | { arena: string }) => {
    const arenaValue = typeof arena === 'string' ? arena : arena.arena;
    return useQuery<Fellowship | null>({
      queryKey: ['fellowship', 'arena', arenaValue],
      queryFn: async (): Promise<Fellowship | null> => {
        console.log('Fetching fellowship for arena:', arenaValue);
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/fellowship/public/arena/${arenaValue}`,
          }); // Remove the second parameter to enable credentials by default
          console.log('Fellowship response:', response.data);
          return response.data as Fellowship;
        } catch (e: any) {
          console.error('Error fetching fellowship:', e);
          if (e.status === 404) {
            return null;
          }
          handleApiError(e);
          return null;
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!arenaValue,
    });
  };

  const useRecentChannelSettings = ({ fellowshipId, channelId }: { fellowshipId: number; channelId: number }) =>
    useQuery({
      queryKey: ['GMBackend', 'recentChannelSettings', fellowshipId, channelId],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/user/profile/recent-settings/${fellowshipId}/${channelId}`,
          }); // Remove the second parameter to enable credentials by default
          return response.data as {
            channelId: string | null;
            serverId: string | null;
            inviteLink: string | null;
            serverName: string | null;
            categoryId: string | null;
            defaultChannelId: string;
            defaultServerId: string | null;
            defaultServerName: string | null;
            defaultInviteLink: string | null;
            defaultCategoryId: string | null;
          };
        } catch (e: any) {
          if (e.status === 404) {
            return null;
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && !!channelId,
    });

  const useThreadParticipant = ({ threadId, fellowshipId }: { threadId: number; fellowshipId: number }) =>
    useQuery({
      queryKey: ['GMBackend', 'threadParticipant', threadId, fellowshipId],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/user/profile/thread/${threadId}/${fellowshipId}`,
          });
          return response.data as {
            channelId?: number;
          };
        } catch (e: any) {
          if (e.status === 404) {
            return { channelId: null };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!threadId && !!fellowshipId,
    });

  const useThreadList = ({
    fellowshipId,
    page = 1,
    limit = 20,
    auth = false,
    myThreads = false,
    myCreatedThreads = false,
    gamePhase,
    enabled = true,
  }: {
    fellowshipId: number;
    page?: number;
    limit?: number;
    auth?: boolean;
    myThreads?: boolean;
    myCreatedThreads?: boolean;
    gamePhase?: string;
    enabled?: boolean;
  }) =>
    useQuery({
      queryKey: ['GMBackend', 'threadList', fellowshipId, page, limit, auth, myThreads, myCreatedThreads, gamePhase],
      queryFn: async () => {
        try {
          const params: any = { page, limit };
          if (myThreads) params.myThreads = 'true';
          if (myCreatedThreads) params.myCreatedThreads = 'true';
          if (gamePhase) params.gamePhase = gamePhase;

          // Auto-enable authentication if filtering options are used
          const requiresAuth = auth || myThreads || myCreatedThreads;

          const response = await http(
            {
              method: 'GET',
              url: `${ApiUrl}/thread/read/list/${fellowshipId}`,
              params,
            },
            !requiresAuth // Only disable credentials if no auth is required
          );
          return response.data as {
            threads: Array<{
              id: string;
              fellowshipId: string;
              rank: number | null;
              instanceAddress: string;
              owner: string;
              gamePhase: string;
              threadType: string;
              metadata: any;
              createdAt: string;
              modifiedAt: string;
              code: string;
              isParticipating: boolean;
              winnerAddress: string | null;
              winnerUsername: string | null;
              nextAction: string | null;
              connectCode: string | null;
              threadGroupId: number | null;
              threadGroup: {
                id: number;
                name: string;
                code: string;
                description: string | null;
              } | null;
              serverInfo: Array<{
                serverName: string;
                serverId: string;
                inviteLink: string;
                providerName: string;
              }>;
              participantCount: number;
            }>;
            pagination: {
              page: number;
              limit: number;
              hasMore: boolean;
            };
          };
        } catch (e: any) {
          if (e.status === 404) {
            return {
              threads: [],
              pagination: { page: 1, limit: 20, hasMore: false },
            };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && enabled,
    });

  const useUserRolesInFellowship = ({
    fellowshipId,
    userAddress,
    enabled = true,
  }: {
    fellowshipId: number;
    userAddress: string;
    enabled?: boolean;
  }) =>
    useQuery({
      queryKey: ['GMBackend', 'role', fellowshipId, userAddress],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/fellowship/member/${fellowshipId}/role`,
            params: { userAddress },
          }).then(
            (r) =>
              r.data as {
                roles: string[];
                fellowshipId: number;
                userAddress: string;
              }
          );

          // Calculate helper booleans from roles array
          const isOwner = response.roles.includes('owner');
          const isAdmin = response.roles.includes('admin');
          const isMember = response.roles.includes('member');

          return {
            ...response,
            isOwner,
            isAdmin,
            isMember,
          };
        } catch (e: any) {
          if (e.status === 404 || e.status === 403 || e.status === 401) {
            return {
              roles: [],
              isOwner: false,
              isAdmin: false,
              isMember: false,
              fellowshipId,
              userAddress: userAddress || '',
            };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && !!userAddress && enabled,
    });

  const createRoleInviteMutation = useMutation({
    mutationFn: async ({
      fellowshipId,
      roleId,
      validityDays = 7,
    }: {
      fellowshipId: number;
      roleId: number;
      validityDays?: number;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/fellowship/admin/${fellowshipId}/role/invite`,
        data: { roleId, validityDays },
      });
    },
    onSuccess: (data) => {
      notification('Invite link created successfully!', 'success', `Code: ${data.data.inviteCode}`);
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const createMemberInviteMutation = useMutation({
    mutationFn: async ({
      fellowshipId,
      userEmail,
      validityDays = 7,
    }: {
      fellowshipId: number;
      userEmail: string;
      validityDays?: number;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/fellowship/admin/${fellowshipId}/invite/link/user`,
        data: { userEmail, validityDays },
      });
    },
    onSuccess: (data) => {
      notification('Member invite ready!', 'success', `For: ${data.data.userEmail || 'user'}`);
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const createMemberInvitesBatchMutation = useMutation({
    mutationFn: async ({
      fellowshipId,
      userEmails,
      validityDays = 7,
    }: {
      fellowshipId: number;
      userEmails: string[];
      validityDays?: number;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/fellowship/admin/${fellowshipId}/invite/link/users`,
        data: { userEmails, validityDays },
      });
    },
    onSuccess: (data) => {
      const summary = data.data.summary;
      notification(
        'Batch invites processed!',
        'success',
        `${summary.successful} successful, ${summary.failed} failed out of ${summary.total}`
      );
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const getRegistrarSignatureMutation = useMutation({
    mutationFn: async ({ inviteCode }: { inviteCode: string }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/user/actions/registrar/sign`,
        data: { inviteCode },
      });
    },
    onSuccess: (data) => {
      return data.data;
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const useActiveUBIUsers = ({ fellowshipId, enabled = true }: { fellowshipId: number; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'activeUBIUsers', fellowshipId],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/fellowship/admin/${fellowshipId}/ubi/active-users`,
          });
          return response.data as {
            fellowshipId: number;
            totalUsers: number;
            usersWithPubKeys: number;
            users: Array<{
              address: string;
              username: string | null;
              publicKey: string | null;
              discord: string | null;
              telegram: string | null;
              farcaster: string | null;
              google: string | null;
              lastClaimDate: string;
              lastClaimBlockTimestamp: string;
            }>;
          };
        } catch (e: any) {
          if (e.status === 404 || e.status === 400) {
            return {
              fellowshipId,
              totalUsers: 0,
              usersWithPubKeys: 0,
              users: [],
            };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && enabled,
    });

  const createAutomatedGameMutation = useMutation({
    mutationFn: async ({ fellowshipId, gameCount, gameParams, threadGroupId }: CreateAutomatedGamePayload) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/fellowship/admin/${fellowshipId}/automated-game/create`,
        data: {
          gameCount: gameCount ?? 1,
          gameParams: gameParams || {},
          threadGroupId,
        },
      });
    },
    onSuccess: (data, variables) => {
      const created = data.data.created || 1;
      const failed = data.data.failed || 0;
      const codes = data.data.codes || [data.data.code];

      if (created > 1) {
        notification(
          `${created} automated game(s) created successfully!${failed > 0 ? ` (${failed} failed)` : ''}`,
          'success',
          `Codes: ${codes.join(', ')}`
        );
      } else {
        notification('Automated game created successfully!', 'success', `Code: ${codes[0] || data.data.code}`);
      }

      // Invalidate thread list to show the new game(s)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'GMBackend' &&
          query.queryKey[1] === 'threadList' &&
          query.queryKey[2] === variables.fellowshipId,
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadGroups', variables.fellowshipId],
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const joinPlayerToGameMutation = useMutation({
    mutationFn: async ({
      fellowshipId,
      gameId,
      playerAddress,
    }: {
      fellowshipId: number;
      gameId: number;
      playerAddress: string;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/fellowship/admin/${fellowshipId}/game/${gameId}/join-player`,
        data: { playerAddress },
      });
    },
    onSuccess: (data, variables) => {
      notification(
        'Player joined successfully!',
        'success',
        `Player ${variables.playerAddress.slice(0, 6)}...${variables.playerAddress.slice(-4)} joined game ${
          variables.gameId
        }`
      );
      // Invalidate fellowship participants to refresh the games list
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'fellowshipParticipants', variables.fellowshipId],
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'myActiveThreads'],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'GMBackend' &&
          query.queryKey[1] === 'threadList' &&
          query.queryKey[2] === variables.fellowshipId,
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const useFellowshipParticipants = ({ fellowshipId, enabled = true }: { fellowshipId: number; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'fellowshipParticipants', fellowshipId],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/fellowship/admin/${fellowshipId}/participants/all`,
          });
          return response.data as {
            fellowshipId: number;
            participants: Array<{
              participant: string;
              games: Array<{
                id: string;
                gamePhase: string;
                threadType: string;
                metadata: any;
                createdAt: string;
                code: string;
              }>;
            }>;
          };
        } catch (e: any) {
          if (e.status === 404) {
            return {
              fellowshipId,
              participants: [],
            };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && enabled,
    });

  const useThreadGroups = ({ fellowshipId, enabled = true }: { fellowshipId: number; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'threadGroups', fellowshipId],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/fellowship/admin/${fellowshipId}/groups`,
          });
          return response.data as {
            fellowshipId: number;
            groups: Array<{
              threadGroupId: number;
              name: string | null;
              description: string | null;
            }>;
          };
        } catch (e: any) {
          if (e.status === 404) {
            return {
              fellowshipId,
              groups: [],
            };
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!fellowshipId && enabled,
    });

  const updateThreadGroupMutation = useMutation({
    mutationFn: async ({
      threadId,
      fellowshipId,
      threadGroupId,
    }: {
      threadId: number;
      fellowshipId: number;
      threadGroupId: number | null;
    }) => {
      return await http({
        method: 'POST',
        url: `${ApiUrl}/thread/owner/store`,
        data: {
          id: threadId,
          fellowshipId,
          threadGroupId,
        },
      });
    },
    onSuccess: (data, variables) => {
      notification(
        'Thread group updated successfully!',
        'success',
        `Thread ${variables.threadId} ${variables.threadGroupId ? 'added to group' : 'removed from group'}`
      );
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'GMBackend' &&
          query.queryKey[1] === 'threadList' &&
          query.queryKey[2] === variables.fellowshipId,
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadGroups', variables.fellowshipId],
      });
      queryClient.invalidateQueries({
        queryKey: ['GMBackend', 'threadByGroupCode'],
      });
    },
    onError: (e: any) => {
      handleApiError(e);
    },
  });

  const useThreadByGroupCode = ({ groupCode, enabled = true }: { groupCode: string; enabled?: boolean }) =>
    useQuery({
      queryKey: ['GMBackend', 'threadByGroupCode', groupCode],
      queryFn: async () => {
        try {
          const response = await http({
            method: 'GET',
            url: `${ApiUrl}/thread/restricted/group/${groupCode}`,
          });
          return response.data as {
            threadId: number;
            fellowshipId: number;
            code: string;
          };
        } catch (e: any) {
          if (e.status === 404) {
            return null;
          }
          handleApiError(e);
        }
      },
      ...queryCacheProps,
      refetchInterval: false,
      enabled: !!groupCode && enabled,
    });

  return {
    useMyProposalId,
    useUserProfile,
    useUserInfo,
    useMyActiveThreads,
    useLastThreadParticipantChannel,
    useDiscordCheckSubscription,
    useTelegramCheckSubscription,
    attestProposalMutation,
    submitProposalMutation,
    attestVoteMutation,
    submitVotesMutation,
    attestJoinGameMutation,
    uploadToIpfsMutation,
    storeUserMutation,
    storeThreadMutation,
    deleteThreadMutation,
    storeThreadParticipantMutation,
    deleteThreadParticipantMutation,
    endTurnMutation,
    useFellowshipData,
    useThreadByCode,
    useChains,
    useValidateJoinRequirement,
    useValidateSpotifyTrack,
    useFellowshipByArena,
    useRecentChannelSettings,
    useThreadParticipant,
    useThreadList,
    useUserRolesInFellowship,
    createRoleInviteMutation,
    createMemberInviteMutation,
    createMemberInvitesBatchMutation,
    getRegistrarSignatureMutation,
    useActiveUBIUsers,
    createAutomatedGameMutation,
    joinPlayerToGameMutation,
    useFellowshipParticipants,
    useThreadGroups,
    useThreadByGroupCode,
    updateThreadGroupMutation,
  };
};

export default useGameMasterBackend;
